import { TrashIcon } from "@phosphor-icons/react";
import type {
  CellStyle,
  ColDef,
  ICellRendererParams,
  SortChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArticleMentionText from "@/components/ui/article-card/article-mention-text";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import TVEyesPlayer from "@/components/ui/TVEyesPlayer";
import useMediaFilterLabelMaps from "@/hooks/useMediaFilterLabelMaps";
import { agGridTheme } from "@/lib/ag-grid-theme";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import useDiscoveryKeyMessageStore from "@/store/discovery/discovery-key-messages/discovery-key-message.slice";
import { useDiscoveryContext } from "@/store/discovery/discovery-params.context";

import PodcastEpisodeCellRenderer from "./PodcastEpisodeCellRenderer";
import {
  createPodcastEpisodesUnifiedOverlay,
  type PodcastEpisodeRowData,
} from "./PodcastEpisodesTableStates";
import {
  createPodcastEpisodesDatasource,
  type PodcastEpisodeOrdering,
  startNewPodcastEpisodesDatasourceSession,
} from "./podcastEpisodesTableDatasource";

const DEFAULT_ORDERING: PodcastEpisodeOrdering = {
  publishDate: "DESC_NULLS_LAST",
};

/** Maps AG Grid column IDs to their corresponding {@link PodcastEpisodeOrdering} field names. */
const COL_ID_TO_ORDERING_FIELD: Record<string, keyof PodcastEpisodeOrdering> = {
  PUBLISH_DATE: "publishDate",
  TITLE: "title",
  SOURCE_TITLE: "sourceTitle",
  AUTHOR: "author",
  ESTIMATED_AUDIENCE: "estimatedAudience",
};

const CACHE_BLOCK_SIZE = 50;
const MAX_BLOCKS_IN_CACHE = 10;
const INITIAL_ROW_COUNT = 5;
const ROW_HEIGHT = 80;

const formatDateTime = (value: unknown): string => {
  const str = value as string | null;
  if (!str) return "-";
  try {
    return new Date(str).toLocaleString();
  } catch {
    return str;
  }
};

const PodcastEpisodesTable = () => {
  const { getLabelMap, isLoading: isMediaFilterLabelsLoading } =
    useMediaFilterLabelMaps();
  const podcastLanguageMap = getLabelMap("PODCAST_LANGUAGE");
  const podcastCountryMap = getLabelMap("PODCAST_COUNTRY");

  const gridRef = useRef<AgGridReact>(null);
  const isInitialMount = useRef(true);

  const isFetchingEpisodes = useDiscoveryContentManagementStore(
    (state) => state.isFetchingPodcastEpisodes,
  );
  const error = useDiscoveryContentManagementStore(
    (state) => state.podcastEpisodesError,
  );
  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalPodcastEpisodesCount,
  );
  const isInitialLoading = useDiscoveryContentManagementStore(
    (state) => state.isPodcastEpisodesInitialLoading,
  );
  const searchTerm = useDiscoveryContentManagementStore(
    (state) => state.podcastEpisodesSearchTerm,
  );

  const setIsPodcastExcludeModalOpen = useDiscoveryContentManagementStore(
    (state) => state.setIsPodcastExcludeModalOpen,
  );
  const setPendingPodcastExclusionIds = useDiscoveryContentManagementStore(
    (state) => state.setPendingPodcastExclusionIds,
  );
  const setPodcastExcludeSuccessCallback = useDiscoveryContentManagementStore(
    (state) => state.setPodcastExcludeSuccessCallback,
  );
  const setSelectedPodcastEpisodeIds = useDiscoveryContentManagementStore(
    (state) => state.setSelectedPodcastEpisodeIds,
  );

  const { feedId, startDate, endDate } = useDiscoveryContext();
  const podcastKeyMessageIds = useDiscoveryKeyMessageStore(
    (state) => state.selectedPodcastKeyMessageIds,
  );

  const [ordering, setOrdering] =
    useState<PodcastEpisodeOrdering>(DEFAULT_ORDERING);

  const filters = useMemo(
    () => ({
      ...(feedId ? { feedId } : {}),
      ...(startDate && endDate
        ? { publishDateAfter: startDate, publishDateBefore: endDate }
        : {}),
      ...(searchTerm ? { searchMatch: searchTerm } : {}),
      ...(podcastKeyMessageIds.length > 0
        ? { keyMessageIds: podcastKeyMessageIds }
        : {}),
    }),
    [feedId, startDate, endDate, searchTerm, podcastKeyMessageIds],
  );

  useEffect(() => {
    startNewPodcastEpisodesDatasourceSession();
  }, [filters, ordering]);

  const datasource = useMemo(() => {
    return createPodcastEpisodesDatasource(
      () => filters,
      () => ordering,
    );
  }, [filters, ordering]);

  const handleRetry = useCallback(() => {
    gridRef.current?.api?.refreshInfiniteCache();
  }, []);

  // Register the grid-refresh callback so the store-connected modal can
  // trigger AG Grid side-effects without holding a reference to the grid API.
  useEffect(() => {
    setPodcastExcludeSuccessCallback(() => {
      gridRef.current?.api?.deselectAll();
      startNewPodcastEpisodesDatasourceSession();
      gridRef.current?.api?.purgeInfiniteCache();
    });
    return () => setPodcastExcludeSuccessCallback(null);
  }, [setPodcastExcludeSuccessCallback]);

  const handleSelectionChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    const selected =
      gridRef.current.api.getSelectedRows() as PodcastEpisodeRowData[];
    setSelectedPodcastEpisodeIds(selected.map((r) => r.ID));
  }, [setSelectedPodcastEpisodeIds]);

  const shouldShowOverlay =
    !isFetchingEpisodes && !error && !isInitialLoading && totalCount === 0;
  const shouldShowErrorOverlay = !isFetchingEpisodes && !!error;

  const UnifiedOverlay = useMemo(
    () => createPodcastEpisodesUnifiedOverlay(handleRetry),
    [handleRetry],
  );

  useEffect(() => {
    if (!gridRef.current?.api) return;
    if (shouldShowErrorOverlay || shouldShowOverlay) {
      gridRef.current.api.showNoRowsOverlay();
    } else {
      gridRef.current.api.hideOverlay();
    }
    if (shouldShowErrorOverlay) {
      gridRef.current.api.setRowCount(0, true);
    }
  }, [shouldShowOverlay, shouldShowErrorOverlay]);

  // Reset ordering to default when feedId changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (gridRef.current?.api) {
      gridRef.current.api.purgeInfiniteCache();
    }
    setOrdering(DEFAULT_ORDERING);
  }, [feedId]);

  const handleSortChanged = (event: SortChangedEvent) => {
    if (!event.api) return;

    const columnStates = event.api.getColumnState();
    const sortedCol = columnStates.find((col) => col.sort !== null);

    if (!sortedCol?.colId) {
      setOrdering(DEFAULT_ORDERING);
      return;
    }

    const direction =
      sortedCol.sort === "asc" ? "ASC_NULLS_LAST" : "DESC_NULLS_LAST";

    const field = COL_ID_TO_ORDERING_FIELD[sortedCol.colId];
    setOrdering(field ? { [field]: direction } : DEFAULT_ORDERING);
  };

  const handleExcludeSingleRow = useCallback(
    (id: string) => {
      setPendingPodcastExclusionIds([id]);
      setIsPodcastExcludeModalOpen(true);
    },
    [setPendingPodcastExclusionIds, setIsPodcastExcludeModalOpen],
  );

  const columnDefs: ColDef<PodcastEpisodeRowData>[] = [
    {
      colId: "ACTIONS",
      headerName: "",
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      resizable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 12px",
        // AG Grid's default line-height causes the icon to sit above centre;
        // 0.5 collapses it so the flex alignment takes full effect.
        lineHeight: 0.5,
      },
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        return (
          <button
            type="button"
            aria-label="Exclude episode"
            className="text-gray-400 hover:text-red-600 transition-colors"
            onClick={() => handleExcludeSingleRow(params.data?.ID ?? "")}
          >
            <TrashIcon size={16} aria-hidden />
          </button>
        );
      },
    },

    {
      colId: "IMAGE_URL",
      field: "IMAGE_URL",
      headerName: "",
      width: 64,
      minWidth: 64,
      maxWidth: 64,
      sortable: false,
      resizable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 8px",
      },
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        if (!params.value) return null;
        return (
          <img
            src={params.value}
            alt={params.data?.SOURCE_TITLE ?? "Podcast artwork"}
            className="h-10 w-10 rounded object-cover"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        );
      },
    },
    {
      colId: "PLAYER_URL",
      headerName: "",
      width: 48,
      minWidth: 48,
      maxWidth: 48,
      sortable: false,
      resizable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 12px",
      },
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        return (
          <TVEyesPlayer
            id={params.data.ID}
            feedId={feedId ?? 0}
            type="podcast"
            variant="headphones"
            meta={{
              title: params.data.TITLE,
              subtitle: params.data.SOURCE_TITLE,
            }}
          />
        );
      },
    },
    {
      colId: "TITLE",
      field: "TITLE",
      headerName: "Title",
      width: 260,
      minWidth: 200,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => (
        <PodcastEpisodeCellRenderer params={params} />
      ),
    },
    {
      colId: "SUBTITLE",
      field: "SUBTITLE",
      headerName: "Subtitle",
      width: 200,
      minWidth: 150,
      resizable: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => (
        <PodcastEpisodeCellRenderer params={params} />
      ),
    },
    {
      colId: "SOURCE_TITLE",
      field: "SOURCE_TITLE",
      headerName: "Show",
      width: 200,
      minWidth: 150,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => (
        <PodcastEpisodeCellRenderer params={params} />
      ),
    },
    {
      colId: "PUBLISH_DATE",
      field: "PUBLISH_DATE",
      headerName: "Publish Date",
      width: 180,
      minWidth: 160,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => (
        <PodcastEpisodeCellRenderer
          params={params}
          formatter={formatDateTime}
        />
      ),
    },
    {
      colId: "DESCRIPTION",
      field: "DESCRIPTION",
      headerName: "Description",
      width: 300,
      minWidth: 200,
      resizable: true,
      sortable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        whiteSpace: "nowrap",
      },
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        const val = params.value as string | null;
        if (!val) return "-";
        const stripped = val.replace(/<[^>]+>/g, "");
        return <span title={stripped}>{stripped}</span>;
      },
    },
    {
      colId: "AUTHOR",
      field: "AUTHOR",
      headerName: "Author",
      width: 180,
      minWidth: 140,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => (
        <PodcastEpisodeCellRenderer params={params} />
      ),
    },
    {
      colId: "ESTIMATED_AUDIENCE",
      field: "ESTIMATED_AUDIENCE",
      headerName: "Audience",
      width: 150,
      minWidth: 120,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => (
        <PodcastEpisodeCellRenderer params={params} numberFormat="commas" />
      ),
    },
    {
      colId: "LANGUAGE",
      field: "LANGUAGE",
      headerName: "Language",
      width: 160,
      minWidth: 130,
      resizable: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        const val = params.value as string | null;
        if (!val || val === "UNKNOWN" || val === "OTHER") return "-";
        return isMediaFilterLabelsLoading && !podcastLanguageMap.has(val) ? (
          <Skeleton className="h-5 w-full" />
        ) : (
          (podcastLanguageMap.get(val) ?? "-")
        );
      },
    },
    {
      colId: "SOURCE_COUNTRY",
      field: "SOURCE_COUNTRY",
      headerName: "Country",
      width: 150,
      minWidth: 120,
      resizable: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        const val = params.value as string | null;
        if (!val || val === "UNKNOWN") return "-";
        return isMediaFilterLabelsLoading && !podcastCountryMap.has(val) ? (
          <Skeleton className="h-5 w-full" />
        ) : (
          (podcastCountryMap.get(val) ?? "-")
        );
      },
    },
    {
      colId: "DURATION",
      field: "DURATION",
      headerName: "Duration",
      width: 130,
      minWidth: 110,
      resizable: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => (
        <PodcastEpisodeCellRenderer params={params} />
      ),
    },
    {
      colId: "TRANSCRIPT_EXCERPT",
      field: "TRANSCRIPT_EXCERPT",
      headerName: "Excerpt",
      width: 400,
      minWidth: 250,
      resizable: true,
      sortable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        whiteSpace: "normal",
        lineHeight: "1.4",
        paddingTop: "8px",
        paddingBottom: "8px",
      },
      cellRenderer: (params: ICellRendererParams<PodcastEpisodeRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        const val = params.value as string | null;
        if (!val) return "-";
        // Extract the first matched term from the <em> tag the podcast transcript feed wraps around it.
        const termMatch = val.match(/<em>(.*?)<\/em>/i);
        const term = termMatch ? termMatch[1] : "";
        const snippet = val.replace(/<[^>]+>/g, "");
        return (
          <ArticleMentionText
            snippet={snippet}
            term={term}
            className="text-inherit"
          />
        );
      },
    },
  ];

  return (
    <section
      className="w-full h-full relative"
      aria-busy={isFetchingEpisodes}
      aria-live="polite"
      aria-label={isFetchingEpisodes ? "Loading podcast episodes" : undefined}
      style={{ "--ag-grid-min-height": "100px" } as React.CSSProperties}
    >
      <AgGridReact<PodcastEpisodeRowData>
        ref={gridRef}
        columnDefs={columnDefs}
        defaultColDef={{
          sortable: true,
          resizable: true,
          cellStyle: { display: "flex", alignItems: "center" },
        }}
        rowModelType="infinite"
        datasource={datasource}
        getRowId={(params) => params.data.ID}
        cacheBlockSize={CACHE_BLOCK_SIZE}
        maxBlocksInCache={MAX_BLOCKS_IN_CACHE}
        infiniteInitialRowCount={INITIAL_ROW_COUNT}
        rowHeight={ROW_HEIGHT}
        theme={agGridTheme}
        enableCellTextSelection
        suppressDragLeaveHidesColumns
        pagination
        paginationPageSize={50}
        paginationPageSizeSelector={[10, 20, 50, 100]}
        loadingOverlayComponent={() => null}
        noRowsOverlayComponent={UnifiedOverlay}
        onSortChanged={handleSortChanged}
        onSelectionChanged={handleSelectionChanged}
        selectionColumnDef={{
          width: 40,
          minWidth: 40,
          maxWidth: 40,
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 12px",
          } as CellStyle,
        }}
        rowSelection={{
          mode: "multiRow",
          checkboxes: true,
        }}
      />
    </section>
  );
};

export default PodcastEpisodesTable;
